import {Injectable} from '@angular/core';
import {
  CustomerContact,
  SimplePaginationUtils,
  Ticket,
  TicketContactSearch,
  TicketSearch,
  TicketService,
  TicketStatus,
  TicketType,
} from '@lifeislife/lifeislife-domain';
import {BehaviorSubject, combineLatest, concat, Observable, of, timer} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, map, publishReplay, refCount, switchMap} from 'rxjs/operators';
import {CustomerContextService} from './customer-context-service';
import {isSameRef} from './utils';

@Injectable({
  providedIn: 'root',
})
export class PendingTicketDraftService {

  private pendingTicketDraft$: Observable<Ticket | null>;
  private reloadSource$ = new BehaviorSubject<boolean>(false);

  constructor(
    private customerContextService: CustomerContextService,
    private ticketService: TicketService,
  ) {
    const distinctCustomerContacts$ = this.customerContextService.activeCustomerContact$.pipe(
      distinctUntilChanged(isSameRef),
    );
    this.pendingTicketDraft$ = combineLatest([distinctCustomerContacts$, this.reloadSource$]).pipe(
      debounceTime(2000), // Ensure event handler have ran on the backend side
      switchMap(r => this.findActiveDraftForCustomerContact$(r[0])),
      publishReplay(1), refCount(),
    );
  }

  reload() {
    this.reloadSource$.next(true);
  }

  getPendingDraft$(ticketType?: TicketType) {
    return this.pendingTicketDraft$.pipe(
      filter(d => d == null || ticketType == null || d.ticketType === ticketType),
      publishReplay(1), refCount(),
    );
  }

  private findActiveDraftForCustomerContact$(customerContact: CustomerContact | null): Observable<Ticket | null> {
    if (customerContact == null) {
      return of(null);
    }
    const ticketSearch: TicketSearch = {
      ticketContactSearch: {
        contactSearch: {
          exactContactRef: customerContact.contactRef,
        },
      } as TicketContactSearch,
      customerSearch: {
        exactCustomerRef: customerContact.customerRef,
      },
      ticketStatusList: [TicketStatus.DRAFT],
    } as TicketSearch;
    const pagination = SimplePaginationUtils.newPagination(1);

    const results$ = of(null).pipe(
      switchMap(() => this.ticketService.searchTickets$(ticketSearch, pagination)),
      map(r => r.count === 0 ? null : r.list[0]),
    );
    return results$;
  }

}
